import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/auth/AuthContext';
import { createUser } from '../services/userService';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Alert,
  Divider,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  PersonAdd,
  Person,
  School,
  Badge,
  Email,
  Lock,
  AdminPanelSettings,
  Engineering,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { deepPurple, green, red } from '@mui/material/colors';

const CreateUsers = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    studentId: '',
    name: '',
    email: '',
    role: 'user',
    program: '',
    status: 'inactive', // default to 'inactive' for Rider
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(true);

  // Determine available roles based on current user's role
  const availableRoles = user?.role === 'admin' 
    ? [
        { value: 'user', label: 'Rider', icon: <Person /> },
        { value: 'student employee', label: 'Student Employee', icon: <Engineering /> },
        { value: 'admin', label: 'Admin', icon: <AdminPanelSettings /> }
      ]
    : [
        { value: 'user', label: 'Rider', icon: <Person /> }
      ];

  // Update password fields visibility when role or status changes
  useEffect(() => {
    const shouldShow = !(formData.role === 'user' && formData.status === 'inactive');
    setShowPasswordFields(shouldShow);
  }, [formData.role, formData.status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      if (value === 'user') {
        setFormData({
          ...formData,
          role: value,
          status: 'inactive'
        });
      } else {
        setFormData({
          ...formData,
          role: value,
          status: 'active'
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.studentId) newErrors.studentId = 'ID is required';
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.program) newErrors.program = 'Program is required';
    
    // Validate password fields only if they're shown
    if (showPasswordFields) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
  
    try {
      const payload = {
        email: formData.email,
        student_id: formData.studentId,
        name: formData.name,
        program: formData.program,
        role: formData.role,
        status: formData.status
      };
  
      // Only add password if required
      if (showPasswordFields) {
        payload.user_pwd = formData.password;
      }
  
      
      const result = await createUser(payload);
      
      if (result.success) {
        setSuccess(true);
        // Reset form
      } else {
        setApiError(result.message);
      }
    } catch (error) {
      setApiError(error.response?.data?.error || 'Failed to create user');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative'
    }}>
      <Navbar />
      <Container maxWidth="md" sx={{ pt: 10, mt: 4, mb: 6, flex: 1, pb: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <Avatar sx={{ bgcolor: deepPurple[100], width: 56, height: 56 }}>
              <PersonAdd sx={{ color: deepPurple[800], fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: deepPurple[800] }}>
                Create New User
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {user?.role === 'admin' 
                  ? 'You can create all types of users' 
                  : 'You can create rider accounts'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              User created successfully!
              {formData.role === 'user' && formData.status === 'inactive' && (
                <span> A random password has been generated and emailed to the user.</span>
              )}
            </Alert>
          )}
          {apiError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {apiError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Student/Employee ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={formData.role === 'user' ? 'Student ID' : 'Employee ID'}
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  error={!!errors.studentId}
                  helperText={errors.studentId}
                  InputProps={{
                    startAdornment: <Badge color="primary" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>

              {/* Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  InputProps={{
                    startAdornment: <Person color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: <Email color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>

              {/* Role */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    label="Role"
                  >
                    {availableRoles.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {role.icon}
                          {role.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.role && (
                    <Typography variant="caption" color="error">
                      {errors.role}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Program/Occupation */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={formData.role === 'user' ? 'Program' : 'Occupation'}
                  name="program"
                  value={formData.program}
                  onChange={handleChange}
                  error={!!errors.program}
                  helperText={errors.program}
                  InputProps={{
                    startAdornment: <School color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="active">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: green[500] }} />
                        Active (Can login)
                      </Box>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cancel sx={{ color: red[500] }} />
                        Inactive (Cannot login)
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {formData.role === 'user' && formData.status === 'inactive' 
                    ? 'A random password will be generated and emailed to the user'
                    : 'User must set their own password'}
                </Typography>
              </Grid>

              {/* Password Fields (conditionally shown) */}
              {showPasswordFields && formData.status !== 'inactive' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      error={!!errors.password}
                      helperText={errors.password || 'Minimum 8 characters'}
                      InputProps={{
                        startAdornment: <Lock color="action" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      InputProps={{
                        startAdornment: <Lock color="action" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<PersonAdd />}
                    sx={{
                      bgcolor: deepPurple[600],
                      '&:hover': { bgcolor: deepPurple[800] },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    Create User
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default CreateUsers;